import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Tag, 
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Percent
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminOffers() {
  const { getAuthHeader } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newOffer, setNewOffer] = useState({
    title: '',
    description: '',
    discount_percent: '',
    valid_until: '',
    applicable_plans: []
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/offers`, {
        headers: getAuthHeader()
      });
      setOffers(response.data.offers);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const createOffer = async () => {
    if (!newOffer.title || !newOffer.discount_percent || !newOffer.valid_until) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin/offers`, {
        ...newOffer,
        discount_percent: parseFloat(newOffer.discount_percent)
      }, {
        headers: getAuthHeader()
      });
      setOffers(prev => [...prev, response.data]);
      setShowDialog(false);
      setNewOffer({
        title: '',
        description: '',
        discount_percent: '',
        valid_until: '',
        applicable_plans: []
      });
      toast.success('Offer created');
    } catch (error) {
      toast.error('Failed to create offer');
    }
  };

  const toggleOffer = async (offerId) => {
    try {
      await axios.put(`${API_URL}/api/admin/offers/${offerId}/toggle`, {}, {
        headers: getAuthHeader()
      });
      setOffers(prev => prev.map(o => 
        o.id === offerId ? { ...o, is_active: !o.is_active } : o
      ));
      toast.success('Offer updated');
    } catch (error) {
      toast.error('Failed to update offer');
    }
  };

  const deleteOffer = async (offerId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/offers/${offerId}`, {
        headers: getAuthHeader()
      });
      setOffers(prev => prev.filter(o => o.id !== offerId));
      toast.success('Offer deleted');
    } catch (error) {
      toast.error('Failed to delete offer');
    }
  };

  const togglePlan = (plan) => {
    setNewOffer(prev => ({
      ...prev,
      applicable_plans: prev.applicable_plans.includes(plan)
        ? prev.applicable_plans.filter(p => p !== plan)
        : [...prev.applicable_plans, plan]
    }));
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="grid grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-40 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-offers">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Offers & Discounts</h1>
          <p className="text-navy-600">Create promotional offers for subscriptions</p>
        </div>
        <Button 
          onClick={() => setShowDialog(true)}
          className="bg-navy-900 hover:bg-navy-800 gap-2"
          data-testid="create-offer-btn"
        >
          <Plus className="w-4 h-4" />
          Create Offer
        </Button>
      </div>

      {/* Offers Grid */}
      {offers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Tag className="w-12 h-12 mx-auto mb-4 text-navy-300" />
            <p className="text-navy-600">No offers created yet</p>
            <Button 
              onClick={() => setShowDialog(true)}
              className="mt-4"
            >
              Create Your First Offer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {offers.map((offer) => (
            <Card key={offer.id} className={!offer.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-navy-900">{offer.title}</h3>
                      <Badge className={offer.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-navy-600">{offer.description}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-green-100 px-3 py-1 rounded-full">
                    <Percent className="w-4 h-4 text-green-600" />
                    <span className="font-bold text-green-600">{offer.discount_percent}% OFF</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-navy-500">Valid until:</span>
                  <span className="text-sm font-medium text-navy-700">
                    {new Date(offer.valid_until).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-navy-500">Applicable to:</span>
                  {offer.applicable_plans?.map(plan => (
                    <Badge key={plan} variant="outline">{plan}</Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleOffer(offer.id)}
                    className="gap-2"
                  >
                    {offer.is_active ? (
                      <>
                        <ToggleRight className="w-4 h-4" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-4 h-4" />
                        Activate
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteOffer(offer.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Offer Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="e.g., New Year Sale"
                value={newOffer.title}
                onChange={(e) => setNewOffer(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the offer..."
                value={newOffer.description}
                onChange={(e) => setNewOffer(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Discount Percentage *</Label>
              <Input
                type="number"
                placeholder="e.g., 20"
                value={newOffer.discount_percent}
                onChange={(e) => setNewOffer(prev => ({ ...prev, discount_percent: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Valid Until *</Label>
              <Input
                type="date"
                value={newOffer.valid_until}
                onChange={(e) => setNewOffer(prev => ({ ...prev, valid_until: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Applicable Plans</Label>
              <div className="flex gap-2">
                {['basic', 'premium'].map(plan => (
                  <Button
                    key={plan}
                    type="button"
                    variant={newOffer.applicable_plans.includes(plan) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => togglePlan(plan)}
                    className={newOffer.applicable_plans.includes(plan) ? 'bg-sky-500' : ''}
                  >
                    {plan}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={createOffer} className="flex-1 bg-navy-900 hover:bg-navy-800">
                Create Offer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
